import { Component, createRef } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { useLocation, useParams } from 'react-router-dom'

import { useMapMarkerCalloutQuery } from 'hooks/useQueries'
import {
  trackAccommodationViewed,
  trackAdventureViewed,
} from '../../analytics/map'

import { MarkerPopupWrapper } from '../../components/maps/styled'
import AdventureCard from '../../components/cards/AdventureCard'

import { deserializeSearchQuery } from '../../actions/utils/search'
import styledTheme from '../../styles/theme'

import PlaceholderCard from '../../components/cards/PlaceholderCard'
import ListingCard from '../../components/cards/ListingCard'
import WithSelectEscape from '../../components/SelectEscape/WithSelectEscape'
import { makeItemKey } from '../../dashboard/MyBucketlists/gql/getBucketListItemsIndexHelpers'

import { formatUrlWithFilters } from '../utils'
import MarkerCalloutTabbedView from './MarkerCalloutTabbedView'

const MarkerCalloutWrapper = styled.div`
  position: relative;
  height: 0;
  width: 0;

  transition: transform 300ms ease-out;

  ${({ x, y }) => `
    transform: translate(${x}px, ${y}px);
  `}

  ${({ open }) =>
    open &&
    `
    z-index: 1;
  `}
`

const isListingInBucketList = ({
  listing,
  bucketListItemsIndex,
  bucketListId,
}) => {
  const indexKey = makeItemKey({ itemId: listing.id, itemType: 'Listing' })
  const bucketListIds = bucketListItemsIndex[indexKey] ?? []
  return bucketListIds.includes(bucketListId)
}

const filterStoriesThatAreInCurrentBucketList = ({
  stories,
  bucketListItemsIndex,
  bucketListId,
}) => {
  return stories.filter((story) => {
    const indexKey = makeItemKey({ itemId: story.id, itemType: 'Story' })
    const bucketListIds = bucketListItemsIndex[indexKey] ?? []

    return bucketListIds.includes(bucketListId)
  })
}

class MarkerCallout extends Component {
  constructor() {
    super()
    this.state = {
      tabIndex: 0,
      transform: {
        x: 0,
        y: 0,
      },
    }
    this.isDragging = false
    this.calloutRef = createRef()
    this.analyticsTracked = false
  }

  componentDidMount() {
    const { transform } = this.props
    setTimeout(() => this.setState({ transform }), 0)
  }

  componentDidUpdate() {
    const { open, mapContainerRef } = this.props

    if (open && mapContainerRef.current) {
      mapContainerRef.current.addEventListener(
        'mousedown',
        this.handleMouseDown
      )
      mapContainerRef.current.addEventListener(
        'mousemove',
        this.handleMouseMove
      )
      mapContainerRef.current.addEventListener('mouseup', this.handleMouseUp)
      mapContainerRef.current.addEventListener('click', this.handleMouseUp)
    } else {
      this.removeListeners()
    }
  }

  componentWillUnmount() {
    this.removeListeners()
  }

  removeListeners = () => {
    const { mapContainerRef } = this.props
    if (mapContainerRef.current) {
      mapContainerRef.current.removeEventListener(
        'mousedown',
        this.handleMouseDown
      )
      mapContainerRef.current.removeEventListener(
        'mousemove',
        this.handleMouseMove
      )
      mapContainerRef.current.removeEventListener('mouseup', this.handleMouseUp)
      mapContainerRef.current.removeEventListener('click', this.handleMouseUp)
    }
  }

  handleMouseDown = () => {
    this.isDragging = false
  }

  handleMouseUp = (e) => {
    if (!this.isDragging) {
      const { onClose } = this.props
      if (!this.calloutRef.current.contains(e.target)) {
        onClose()
      }
    }
  }

  handleMouseMove = () => {
    this.isDragging = true
  }

  renderInner = (result, pricing) => {
    const {
      bookingAdventuresIndex,
      bucketListItemsIndex,
      onAddedToBucketList,
      onRemovedFromBucketList,
      open,
      source,
      filters,
    } = this.props

    const shouldDispatchAnalytics = open && this.analyticsTracked !== true

    switch (result.__typename) {
      case 'Listing':
        if (shouldDispatchAnalytics) {
          trackAccommodationViewed({
            listingId: result.id,
            source,
          })
          this.analyticsTracked = true
        }
        return (
          <>
            <WithSelectEscape
              itemId={result.id}
              itemType="Listing"
              bucketListItemsIndex={bucketListItemsIndex}
              onAddedToBucketList={onAddedToBucketList}
              onRemovedFromBucketList={onRemovedFromBucketList}
            >
              <ListingCard
                listing={{
                  ...result,
                  pricing,
                  url: formatUrlWithFilters(filters, result.url),
                }}
                compactCard
              />
            </WithSelectEscape>
          </>
        )

      case 'StoryAdventure':
        if (shouldDispatchAnalytics) {
          trackAdventureViewed({
            adventureId: result.id,
            source,
          })
          this.analyticsTracked = true
        }
        return (
          <WithSelectEscape
            itemId={result.id}
            itemType="Adventure"
            bookingAdventuresIndex={bookingAdventuresIndex}
            bucketListItemsIndex={bucketListItemsIndex}
            onAddedToBucketList={onAddedToBucketList}
            onRemovedFromBucketList={onRemovedFromBucketList}
          >
            <AdventureCard {...result} padded compactCard />
          </WithSelectEscape>
        )

      default:
        return result.__typename
    }
  }

  render() {
    const {
      data,
      loading,
      children,
      open,
      bucketListItemsIndex,
      onPopupInteractionStart,
      onPopupInteractionEnd,
      isOnBucketListRoute,
      bucketListId,
      source,
      filters,
    } = this.props
    const { transform, tabIndex } = this.state

    const { result: originalResult, pricing } = data?.search?.result ?? {
      result: {},
    }

    const filteredStories =
      isOnBucketListRoute && bucketListId && originalResult?.stories
        ? filterStoriesThatAreInCurrentBucketList({
            stories: originalResult.stories,
            bucketListId,
            bucketListItemsIndex,
          })
        : originalResult.stories

    const result = {
      ...originalResult,
      stories: filteredStories,
    }

    const showListing =
      isOnBucketListRoute && result.__typename === 'Listing'
        ? isListingInBucketList({
            listing: result,
            bucketListItemsIndex,
            bucketListId,
          })
        : true

    const hasManyResults = result && result.stories && result.stories.length > 0

    return (
      <MarkerCalloutWrapper ref={this.calloutRef} {...transform} open={open}>
        <ThemeProvider theme={styledTheme}>
          <div>
            {children}
            <MarkerPopupWrapper
              open={open}
              padded
              onMouseEnter={onPopupInteractionStart}
              onMouseLeave={onPopupInteractionEnd}
              onTouchStart={onPopupInteractionStart}
              onTouchEnd={onPopupInteractionEnd}
              onTouchCancel={onPopupInteractionEnd}
            >
              {(loading || !result) && <PlaceholderCard padded />}
              {!loading && result && (
                <div>
                  {hasManyResults ? (
                    <MarkerCalloutTabbedView
                      tabIndex={tabIndex}
                      onTabChange={(index) =>
                        this.setState({ tabIndex: index })
                      }
                      listing={{
                        ...result,
                        pricing,
                        url: formatUrlWithFilters(filters, result.url),
                      }}
                      latestStory={{
                        ...result.stories[0],
                        url: formatUrlWithFilters(
                          filters,
                          result.stories[0].url
                        ),
                      }}
                      bucketListItemsIndex={bucketListItemsIndex}
                      showListing={showListing}
                      source={source}
                    />
                  ) : (
                    this.renderInner(result, pricing)
                  )}
                </div>
              )}
            </MarkerPopupWrapper>
          </div>
        </ThemeProvider>
      </MarkerCalloutWrapper>
    )
  }
}

const MarkerCalloutWithData = (props) => {
  const { id, open, type } = props
  const location = useLocation()
  const { id: bucketListId } = useParams()
  const filters = deserializeSearchQuery(location.search)
  const isOnBucketListRoute = location.pathname.includes('bucketlist')

  const params = {
    ...filters,
  }
  /*
  The mapMarkerCallout query is scoped to `search`, which expects a value for
  the top-level `type` property of either 'accommodation' or 'adventures' -- on the search page this is
  always present because it's toggled between accom and adventures. On the bucket
  list page, both need to be displayed at the same time. Therefore, we derive the high-level
  `type` value from the `type` of the MapResult itself.
  */
  if (!filters.type) {
    params.type = type === 'Adventure' ? 'adventures' : 'accommodation'
  }

  const { data, loading } = useMapMarkerCalloutQuery({
    variables: { params, type, id },
    skip: !open,
  })

  const propsWithData = {
    ...props,
    data,
    loading,
    params,
    filters,
    isOnBucketListRoute,
    bucketListId,
  }

  return <MarkerCallout {...propsWithData} />
}

export default MarkerCalloutWithData
